import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { studentNav } from './navConfig';
import { useAuth } from '@/hooks/useAuth';

export default function StudentLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/giris', { replace: true });
    }
  };

  const firstName = (profile?.full_name ?? 'Öğrenci').split(' ')[0];

  const sidebar = (
    <div className="flex h-full flex-col bg-gradient-to-b from-primary-950 to-primary-900">
      <div className="flex items-center gap-3 px-5 py-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 font-heading text-xl font-extrabold text-primary-950">
          Z
        </div>
        <div className="leading-tight">
          <p className="font-heading text-[15px] font-bold text-white">Zehra Hocayla</p>
          <p className="text-xs font-medium text-accent-300">Matematik</p>
        </div>
      </div>

      <div className="px-5 pb-3">
        <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 font-heading text-sm font-bold text-primary-950">
            {firstName.charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-white">{profile?.full_name ?? 'Öğrenci'}</p>
            <p className="text-xs text-primary-300">Öğrenci</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-thin px-3 py-2">
        {studentNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                isActive
                  ? 'bg-white/10 text-white ring-1 ring-accent-500/30'
                  : 'text-primary-200 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${
                    isActive ? 'bg-accent-500 text-primary-950' : 'bg-white/5 text-primary-300 group-hover:text-white'
                  }`}
                >
                  <i className={`${item.icon} text-base`} />
                </span>
                <span className="whitespace-nowrap">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-4">
        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary-200 transition hover:bg-white/5 hover:text-white cursor-pointer"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-primary-300">
            <i className="ri-logout-box-r-line text-base" />
          </span>
          <span className="whitespace-nowrap">Çıkış Yap</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-50">
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 w-72">{sidebar}</div>
        </div>
      )}

      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">{sidebar}</aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-background-200 bg-background-50/90 backdrop-blur">
          <div className="flex items-center justify-between gap-4 px-4 py-3.5 md:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-700 transition hover:bg-background-100 lg:hidden cursor-pointer"
                aria-label="Menüyü aç"
              >
                <i className="ri-menu-3-line text-xl" />
              </button>
              <p className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Öğrenci Portalı</p>
            </div>

            <button
              type="button"
              className="relative flex h-10 w-10 items-center justify-center rounded-lg text-foreground-700 transition hover:bg-background-100 cursor-pointer"
              aria-label="Bildirimler"
            >
              <i className="ri-notification-3-line text-xl" />
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-accent-500 ring-2 ring-background-50" />
            </button>
          </div>
        </header>

        <main className="px-4 py-6 md:px-6 md:py-8">
          {profile?.active === false ? (
            <div className="mx-auto max-w-2xl py-6">
              <div className="rounded-2xl border border-background-200 bg-white p-10 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-pause-circle-line text-3xl" />
                </div>
                <h1 className="font-heading text-xl font-bold text-foreground-950">Hesabınız pasif durumda</h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
                  Hesabınız şu anda pasif durumda. Ders erişiminiz için öğretmeninizle iletişime geçebilirsiniz.
                </p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}